import React, { useEffect, useState, useMemo } from "react";
import Swipeable from "@/components/Swipeable";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  ArrowLeft, Home, Plus, Search, Phone, Mail, Globe, Copy, Check,
  Pencil, Trash2, ExternalLink, BookOpen, Loader2,
} from "lucide-react";
import { authGet, authPost, authPut, authDelete } from "../utils/auth";
import { toast } from "sonner";

const CATEGORY_ORDER = [
  "All",
  "Insurer RM",
  "TPA / Claims Desk",
  "Broker / IMF Partner",
  "Quotation Platform",
  "Insurer Portal",
  "Other",
];

const CATEGORY_COLOR = {
  "Insurer RM": "text-emerald-700 bg-emerald-500/15 border-emerald-500/30",
  "TPA / Claims Desk": "text-cyan-700 bg-cyan-500/15 border-cyan-500/30",
  "Broker / IMF Partner": "text-[#A87608] bg-[#D4A017]/15 border-[#D4A017]/30",
  "Quotation Platform": "text-violet-700 bg-violet-500/15 border-violet-500/30",
  "Insurer Portal": "text-blue-700 bg-blue-500/15 border-blue-500/30",
  "Other": "text-[#2E3B57] bg-[#EDF1F8]/50 border-[#C9D4E6]",
};

const emptyForm = {
  name: "", company: "", category: "Insurer RM",
  phone: "", email: "", website: "", notes: "",
};

export default function Directory() {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("All");
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => { fetchEntries(); }, []);

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const res = await authGet("/api/advisor/directory");
      if (res.ok) setEntries(res.data?.entries || []);
    } catch (e) { toast.error("Failed to load directory"); }
    setLoading(false);
  };

  const counts = useMemo(() => {
    const c = { All: entries.length };
    for (const cat of CATEGORY_ORDER.slice(1)) c[cat] = 0;
    for (const e of entries) if (c[e.category] !== undefined) c[e.category]++;
    return c;
  }, [entries]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return entries.filter(e => {
      if (activeTab !== "All" && e.category !== activeTab) return false;
      if (!q) return true;
      return (
        (e.name || "").toLowerCase().includes(q) ||
        (e.company || "").toLowerCase().includes(q) ||
        (e.phone || "").toLowerCase().includes(q) ||
        (e.email || "").toLowerCase().includes(q) ||
        (e.website || "").toLowerCase().includes(q) ||
        (e.notes || "").toLowerCase().includes(q)
      );
    });
  }, [entries, activeTab, search]);

  const openAdd = () => {
    const defaultCat = activeTab !== "All" ? activeTab : "Insurer RM";
    setEditing(null);
    setForm({ ...emptyForm, category: defaultCat });
    setShowModal(true);
  };

  const openEdit = (e) => {
    setEditing(e);
    setForm({
      name: e.name || "", company: e.company || "", category: e.category || "Other",
      phone: e.phone || "", email: e.email || "", website: e.website || "", notes: e.notes || "",
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const res = editing
        ? await authPut(`/api/advisor/directory/${editing.id}`, form)
        : await authPost("/api/advisor/directory", form);
      if (res.ok) {
        toast.success(editing ? "Entry updated" : "Entry added");
        setShowModal(false);
        fetchEntries();
      } else {
        toast.error(res.data?.detail || "Failed to save");
      }
    } catch (err) { toast.error("Save failed"); }
    setSaving(false);
  };

  const handleDelete = async (entry) => {
    if (!window.confirm(`Delete "${entry.name}"?`)) return;
    try {
      const res = await authDelete(`/api/advisor/directory/${entry.id}`);
      if (res.ok) {
        toast.success("Entry deleted");
        fetchEntries();
      } else { toast.error("Delete failed"); }
    } catch (e) { toast.error("Delete failed"); }
  };

  const copyText = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1200);
      toast.success("Copied");
    } catch { toast.error("Copy failed"); }
  };

  const openUrl = (url) => {
    if (!url) return;
    const u = url.match(/^https?:\/\//i) ? url : `https://${url}`;
    window.open(u, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="min-h-screen bg-white text-[#1B2A4A]">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-[#C9D4E6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-lg hover:bg-[#EDF1F8]" data-testid="directory-back-btn">
              <ArrowLeft className="h-5 w-5 text-[#1B2A4A]" />
            </button>
            <button onClick={() => navigate("/advisor/dashboard")} className="p-2 rounded-lg hover:bg-[#EDF1F8]" title="Home">
              <Home className="h-5 w-5 text-[#2E3B57]" />
            </button>
            <BookOpen className="h-5 w-5 text-[#A87608] shrink-0" />
            <h1 className="text-lg sm:text-xl font-bold truncate">Directory</h1>
            <span className="text-xs text-[#3A4557] hidden sm:inline">Contacts & Links</span>
          </div>
          <Button
            onClick={openAdd}
            className="bg-[#D4A017] hover:bg-[#D9A82C] text-black font-semibold rounded-full"
            data-testid="directory-add-btn"
          >
            <Plus className="h-4 w-4 mr-1" /> Add
          </Button>
        </div>

        {/* Category tabs */}
        <Swipeable outerClassName="max-w-7xl mx-auto px-4 sm:px-6 pb-3" className="">
          <div className="flex gap-2 min-w-max">
            {CATEGORY_ORDER.map(cat => {
              const active = activeTab === cat;
              const n = counts[cat] ?? 0;
              return (
                <button
                  key={cat}
                  onClick={() => setActiveTab(cat)}
                  data-testid={`directory-tab-${cat.replace(/\s+/g, '-').toLowerCase()}`}
                  className={`px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium border transition ${
                    active
                      ? "bg-[#D4A017] text-black border-[#D4A017]"
                      : "bg-[#EDF1F8]/50 text-[#2E3B57] border-[#C9D4E6] hover:bg-[#EDF1F8]"
                  }`}
                >
                  {cat} <span className={`ml-1 text-[10px] ${active ? "text-black/70" : "text-[#3A4557]"}`}>{n}</span>
                </button>
              );
            })}
          </div>
        </Swipeable>
      </header>

      {/* Search */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#3A4557]" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search name, company, phone, email, URL, notes…"
            className="pl-9 bg-[#EDF1F8]/50 border-[#C9D4E6] text-[#1B2A4A] placeholder:text-[#3A4557]"
            data-testid="directory-search-input"
          />
        </div>
      </div>

      {/* Grid */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-[#A87608]" />
          </div>
        ) : filtered.length === 0 ? (
          <Card className="bg-white/50 border-[#C9D4E6]">
            <CardContent className="py-12 text-center">
              <BookOpen className="h-10 w-10 text-[#3A4557] mx-auto mb-3" />
              <p className="text-[#2E3B57]">
                {search
                  ? "No results for your search."
                  : activeTab === "All"
                    ? "Your directory is empty."
                    : `No entries in "${activeTab}" yet.`}
              </p>
              <Button
                onClick={openAdd}
                className="mt-4 bg-[#D4A017] hover:bg-[#D9A82C] text-black"
                data-testid="directory-add-empty-btn"
              >
                <Plus className="h-4 w-4 mr-1" /> Add first entry
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map(e => (
              <Card
                key={e.id}
                className="bg-white/50 border-[#C9D4E6] hover:border-[#D4A017]/40 transition"
                data-testid={`directory-card-${e.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <h3 className="text-[#1B2A4A] font-semibold text-base truncate">{e.name}</h3>
                      {e.company && <p className="text-[#2E3B57] text-xs truncate">{e.company}</p>}
                    </div>
                    <span
                      className={`shrink-0 text-[10px] px-2 py-0.5 rounded-full border ${CATEGORY_COLOR[e.category] || CATEGORY_COLOR.Other}`}
                    >
                      {e.category}
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="space-y-1.5 mt-3">
                    {e.phone && (
                      <div className="flex items-center gap-2">
                        <a
                          href={`tel:${e.phone}`}
                          className="flex-1 flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#EDF1F8]/60 hover:bg-[#EDF1F8] text-sm text-[#1B2A4A] transition"
                          data-testid={`directory-call-${e.id}`}
                        >
                          <Phone className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                          <span className="truncate">{e.phone}</span>
                        </a>
                        <button
                          onClick={() => copyText(e.phone, `p-${e.id}`)}
                          className="p-1.5 rounded-lg bg-[#EDF1F8]/60 hover:bg-[#EDF1F8]"
                          title="Copy phone"
                          data-testid={`directory-copy-phone-${e.id}`}
                        >
                          {copiedId === `p-${e.id}` ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5 text-[#2E3B57]" />}
                        </button>
                      </div>
                    )}

                    {e.email && (
                      <div className="flex items-center gap-2">
                        <a
                          href={`mailto:${e.email}`}
                          className="flex-1 flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#EDF1F8]/60 hover:bg-[#EDF1F8] text-sm text-[#1B2A4A] transition"
                        >
                          <Mail className="h-3.5 w-3.5 text-blue-600 shrink-0" />
                          <span className="truncate">{e.email}</span>
                        </a>
                        <button
                          onClick={() => copyText(e.email, `e-${e.id}`)}
                          className="p-1.5 rounded-lg bg-[#EDF1F8]/60 hover:bg-[#EDF1F8]"
                          title="Copy email"
                          data-testid={`directory-copy-email-${e.id}`}
                        >
                          {copiedId === `e-${e.id}` ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5 text-[#2E3B57]" />}
                        </button>
                      </div>
                    )}

                    {e.website && (
                      <button
                        onClick={() => openUrl(e.website)}
                        className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#EDF1F8]/60 hover:bg-[#EDF1F8] text-sm text-[#1B2A4A] transition"
                        data-testid={`directory-open-${e.id}`}
                      >
                        <Globe className="h-3.5 w-3.5 text-violet-600 shrink-0" />
                        <span className="truncate flex-1 text-left">{e.website.replace(/^https?:\/\//i, "")}</span>
                        <ExternalLink className="h-3.5 w-3.5 text-[#2E3B57] shrink-0" />
                      </button>
                    )}

                    {!e.phone && !e.email && !e.website && (
                      <p className="text-[#3A4557] text-xs italic">No contact info yet — tap Edit to add.</p>
                    )}
                  </div>

                  {e.notes && (
                    <p className="text-[#2E3B57] text-xs mt-3 whitespace-pre-line line-clamp-3">{e.notes}</p>
                  )}

                  {/* Edit / Delete row */}
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[#C9D4E6]">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEdit(e)}
                      className="flex-1 border-[#C9D4E6] text-[#2E3B57] hover:text-[#1B2A4A] hover:bg-[#EDF1F8]"
                      data-testid={`directory-edit-${e.id}`}
                    >
                      <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(e)}
                      className="border-red-500/40 text-red-600 hover:text-red-700 hover:bg-red-500/10"
                      data-testid={`directory-delete-${e.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Add / Edit modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-white border-[#C9D4E6] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#1B2A4A]">{editing ? "Edit entry" : "Add to Directory"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[#2E3B57] text-xs">Name *</Label>
              <Input
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="e.g., Ravi Sharma / Care Health RM"
                className="bg-[#EDF1F8]/60 border-[#C9D4E6] text-[#1B2A4A]"
                data-testid="directory-form-name"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[#2E3B57] text-xs">Company / Insurer</Label>
                <Input
                  value={form.company}
                  onChange={e => setForm({ ...form, company: e.target.value })}
                  placeholder="e.g., Care Health"
                  className="bg-[#EDF1F8]/60 border-[#C9D4E6] text-[#1B2A4A]"
                  data-testid="directory-form-company"
                />
              </div>
              <div>
                <Label className="text-[#2E3B57] text-xs">Category *</Label>
                <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                  <SelectTrigger className="bg-[#EDF1F8]/60 border-[#C9D4E6] text-[#1B2A4A]" data-testid="directory-form-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#C9D4E6]">
                    {CATEGORY_ORDER.slice(1).map(c => (
                      <SelectItem key={c} value={c} className="text-[#1B2A4A] focus:text-[#1B2A4A] focus:bg-[#EDF1F8]">{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[#2E3B57] text-xs">Phone</Label>
                <Input
                  value={form.phone}
                  onChange={e => setForm({ ...form, phone: e.target.value })}
                  placeholder="9876543210"
                  className="bg-[#EDF1F8]/60 border-[#C9D4E6] text-[#1B2A4A]"
                  data-testid="directory-form-phone"
                />
              </div>
              <div>
                <Label className="text-[#2E3B57] text-xs">Email</Label>
                <Input
                  value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                  placeholder="rm@insurer.com"
                  className="bg-[#EDF1F8]/60 border-[#C9D4E6] text-[#1B2A4A]"
                  data-testid="directory-form-email"
                />
              </div>
            </div>
            <div>
              <Label className="text-[#2E3B57] text-xs">Website / Portal URL</Label>
              <Input
                value={form.website}
                onChange={e => setForm({ ...form, website: e.target.value })}
                placeholder="https://portal.insurer.com"
                className="bg-[#EDF1F8]/60 border-[#C9D4E6] text-[#1B2A4A]"
                data-testid="directory-form-website"
              />
            </div>
            <div>
              <Label className="text-[#2E3B57] text-xs">Notes</Label>
              <Textarea
                value={form.notes}
                onChange={e => setForm({ ...form, notes: e.target.value })}
                placeholder="Login credentials hint, timings, escalation contact, etc."
                className="bg-[#EDF1F8]/60 border-[#C9D4E6] text-[#1B2A4A] min-h-[80px]"
                data-testid="directory-form-notes"
              />
            </div>
            <div className="flex items-center gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowModal(false)}
                className="flex-1 border-[#C9D4E6] text-[#2E3B57] hover:text-[#1B2A4A]"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 bg-[#D4A017] hover:bg-[#D9A82C] text-black font-semibold"
                data-testid="directory-form-save"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : editing ? <Check className="h-4 w-4 mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
                {editing ? "Save changes" : "Add entry"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
